import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { WEB_APIS } from "../../../utils/APIS";
import { GET, POST, PUT, DELETE } from "../../../utils/Methods";
import { IDS } from "../../../utils/IDS";

// Invoice - Get All (Read)
export const GetAll = createAsyncThunk(
    "masters/Invoice/get-all",
    async (filter, { rejectWithValue }) => {
        try {
            const res = await GET(
                `${WEB_APIS.Masters.Invoice}?name=${filter?.name || ""}&page=${filter?.page || IDS.PAGE
                }&per_page=${filter?.per_page || IDS.PERPAGE}&status=${filter?.status || ""}&party_id=${filter?.party_id || ""}`
            );
            return res;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Invoice - Get (Read)
export const GetSingle = createAsyncThunk(
    "masters/Invoice/get",
    async (id, { rejectWithValue }) => {
        try {
            const res = await GET(`${WEB_APIS.Masters.Invoice}/${id}`);
            return res;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const GetInvoiceTransactions = createAsyncThunk(
    "masters/Invoice/get-ts",
    async ({ id, filter }, { rejectWithValue }) => {
        try {
            const res = await GET(`${WEB_APIS.Masters.Invoice}/transactions/${id}?name=${filter?.name || ""}&page=${filter?.page || IDS.PAGE
                }&per_page=${filter?.per_page || IDS.PERPAGE}&filter=${filter?.filter || ""}&sortBy=${filter?.sortBy || ""}`);
            return res?.data;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Invoice - Create (Add)
export const Create = createAsyncThunk(
    "masters/Invoice/create",
    async (data, { rejectWithValue }) => {
        try {
            const res = await POST(`${WEB_APIS.Masters.Invoice}/generate`, data);

            if (res.success) {

                return res; // The new Invoice object
            }

        } catch (error) {

            return rejectWithValue(error.response.data);
        }
    }
);

// Invoice - Update
export const Update = createAsyncThunk(
    "masters/Invoice/update",
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const res = await PUT(`${WEB_APIS.Masters.Invoice}/${id}`, data);
            return res;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

// Invoice - Delete
export const Delete = createAsyncThunk(
    "masters/Invoice/delete",
    async (id, { rejectWithValue }) => {
        try {
            const data = await DELETE(`${WEB_APIS.Masters.Invoice}/${id}`);
            return id; // Return the id of the deleted Invoice
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Invoice - Status
export const Status = createAsyncThunk(
    "masters/Invoice/status",
    async (id, { rejectWithValue }) => {
        try {
            const res = await POST(`${WEB_APIS.Masters.Invoice}/${id}`);
            return res;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);


export const GetAllPendingInvoices = createAsyncThunk(
    "masters/Invoice/get-all-pending",
    async (filter, { rejectWithValue }) => {
        try {
            const res = await GET(
                `${WEB_APIS.Masters.Invoice}/pending?name=${filter?.name || ""}&page=${filter?.page || IDS.PAGE
                }&per_page=${filter?.per_page || IDS.PERPAGE}&status=${filter?.status || ""}&party_id=${filter?.party_id || ""}`
            );
            return res;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);


export const ReceivedPayment = createAsyncThunk(
    "masters/Invoice/receive-payment",
    async (data, { rejectWithValue }) => {
        try {
            const res = await POST(`${WEB_APIS.Masters.Invoice}/record-payment`, data);

            if (res.success) {

                return res; // The new Invoice object
            }

        } catch (error) {

            return rejectWithValue(error.response.data);
        }
    }
);

export const Slice = createSlice({
    name: "Invoice",
    initialState: {
        data: [],
        transactions: [],
        current_page: IDS.PAGE,
        total_pages: IDS.TOTALPAGES,
        per_page: IDS.PERPAGE,
        total: IDS.TOTAL,
        status: IDS.SLICESTATUS.Idle,
        error: null,
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            // Handling all actions for GetSingle, Create, Update, Delete
            .addCase(GetAllPendingInvoices.pending, (state) => {
                state.status = IDS.SLICESTATUS.Loading;
            })
            .addCase(GetAllPendingInvoices.fulfilled, (state, action) => {
                state.status = IDS.SLICESTATUS.Succeeded;
                state.data = action.payload?.data?.data || [];
                state.current_page = action.payload?.data?.current_page || IDS.PAGE;
                state.total_pages = action.payload?.data?.total_pages || IDS.TOTALPAGES;
                state.per_page = action.payload?.data?.per_page || IDS.PERPAGE;
                state.total = action.payload?.data?.total || IDS.TOTAL;
            })
            .addCase(GetAllPendingInvoices.rejected, (state, action) => {
                state.status = IDS.SLICESTATUS.Failed;
                state.error = action.payload;
            })

            .addCase(GetAll.pending, (state) => {
                state.status = IDS.SLICESTATUS.Loading;
            })
            .addCase(GetAll.fulfilled, (state, action) => {
                state.status = IDS.SLICESTATUS.Succeeded;
                state.data = action.payload?.data?.data || [];
                state.current_page = action.payload?.data?.current_page || IDS.PAGE;
                state.total_pages = action.payload?.data?.total_pages || IDS.TOTALPAGES;
                state.per_page = action.payload?.data?.per_page || IDS.PERPAGE;
                state.total = action.payload?.data?.total || IDS.TOTAL;
            })
            .addCase(GetAll.rejected, (state, action) => {
                state.status = IDS.SLICESTATUS.Failed;
                state.error = action.payload;
            })

            .addCase(GetInvoiceTransactions.pending, (state) => {
                state.status = IDS.SLICESTATUS.Loading;
            })
            .addCase(GetInvoiceTransactions.fulfilled, (state, action) => {
                state.status = IDS.SLICESTATUS.Succeeded;
                state.transactions = action.payload?.data || [];
                state.current_page = action.payload?.current_page || IDS.PAGE;
                state.total_pages = action.payload?.total_pages || IDS.TOTALPAGES;
                state.per_page = action.payload?.per_page || IDS.PERPAGE;
                state.total = action.payload?.total || IDS.TOTAL;
            })
            .addCase(GetInvoiceTransactions.rejected, (state, action) => {
                state.status = IDS.SLICESTATUS.Failed;
                state.error = action.payload;
            })

            .addCase(GetSingle.pending, (state) => {
                state.status = IDS.SLICESTATUS.Loading;
            })
            .addCase(GetSingle.fulfilled, (state, action) => {
                state.status = IDS.SLICESTATUS.Succeeded;
            })
            .addCase(GetSingle.rejected, (state, action) => {
                state.status = IDS.SLICESTATUS.Failed;
                state.error = action.payload;
            })

            .addCase(Create.pending, (state) => {
                state.status = IDS.SLICESTATUS.Loading;
            })
            .addCase(Create.fulfilled, (state, action) => {
                state.status = IDS.SLICESTATUS.Succeeded;
                // state.data = action.payload;
                state.data.unshift(action.payload.data);
            })
            .addCase(Create.rejected, (state, action) => {
                state.status = IDS.SLICESTATUS.Failed;
                state.error = action.payload;
            })

            .addCase(Update.pending, (state) => {
                state.status = IDS.SLICESTATUS.Loading;
            })
            .addCase(Update.fulfilled, (state, action) => {
                state.status = IDS.SLICESTATUS.Succeeded;
                // state.data = action.payload;
                const index = state.data.findIndex(
                    (Invoice) => Invoice.id === action.payload?.data?.id
                );
                if (index !== -1) {
                    state.data[index] = action.payload.data;
                }
            })
            .addCase(Update.rejected, (state, action) => {
                state.status = IDS.SLICESTATUS.Failed;
                state.error = action.payload;
            })

            .addCase(Delete.pending, (state) => {
                state.status = IDS.SLICESTATUS.Loading;
            })
            .addCase(Delete.fulfilled, (state, action) => {
                state.status = IDS.SLICESTATUS.Succeeded;
                // state.data = action.payload;
                state.data = state.data.filter(
                    (Invoice) => Invoice.id !== action.payload.id
                ); // Remove the Invoice with the deleted id
            })
            .addCase(Delete.rejected, (state, action) => {
                state.status = IDS.SLICESTATUS.Failed;
                state.error = action.payload;
            })
            //
            .addCase(Status.pending, (state) => {
                state.status = IDS.SLICESTATUS.Loading;
            })
            .addCase(Status.fulfilled, (state, action) => {
                state.status = IDS.SLICESTATUS.Succeeded;
                // state.data = action.payload;
                state.data = state.data.filter(
                    (Invoice) => Invoice.id !== action.payload.id
                ); // Remove the Invoice with the deleted id
            })
            .addCase(Status.rejected, (state, action) => {
                state.status = IDS.SLICESTATUS.Failed;
                state.error = action.payload;
            });
    },
});

// Export actions (if needed in components)
export const { } = Slice.actions;

export default Slice.reducer;
